import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Image,
  Flex,
  Tag,
  AspectRatio,
  Avatar,
  HStack,
  VStack,
  Divider,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  ChakraProps,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { ForumPost, TMediaItem, CommentWithFullAuthor } from "../apis/types";
import { ArrowUpIcon } from "@chakra-ui/icons";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Property } from "csstype";

import {
  MdVerified as VerifiedIcon,
  SentimentSatisfiedIcon,
  VisibilityIcon,
  FavoriteBorderIconReact,
  FavoriteIconReact,
  FaChevronLeft as ChevronLeftIconReact,
  FaChevronRight as ChevronRightIconReact,
} from "./icons";
import {
  useCreateReply,
  useGetCommentsForPost,
  useGetForumPostById,
} from "../apis/experiences";

const commonEmojis = [
  "😀",
  "😂",
  "👍",
  "❤️",
  "🙌",
  "🔥",
  "🤔",
  "😊",
  "🥳",
  "🤯",
  "😭",
  "😡",
  "🙏",
  "💯",
  "✅",
  "💡",
  "🚀",
  "🌟",
  "💬",
  "👀",
];

const formatCount = (num: number | undefined): string => {
  if (num === undefined || num === null || num < 0) return "0";
  if (num < 1000) {
    return num.toString();
  } else if (num < 1000000) {
    const thousands = Math.floor(num / 100) / 10;
    return (
      (thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)) + "K"
    );
  } else if (num < 1000000000) {
    const millions = Math.floor(num / 100000) / 10;
    return (
      (millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)) + "M"
    );
  } else {
    const billions = Math.floor(num / 100000000) / 10;
    return (
      (billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(1)) + "B"
    );
  }
};

interface MediaDisplayProps {
  mediaItem: TMediaItem;
}

const MediaDisplay: React.FC<MediaDisplayProps> = React.memo(
  ({ mediaItem }) => {
    const mediaElementStyle: React.CSSProperties = {
      width: "100%",
      height: "100%",
      objectFit: "cover" as Property.ObjectFit,
      transition: "transform 0.2s ease-in-out",
    };
    const hoverStyle: ChakraProps["sx"] = {
      transform: "scale(1.05)",
    };

    if (mediaItem.type === "image") {
      return (
        <AspectRatio
          ratio={16 / 9}
          w="100%"
          h="100%"
          overflow="hidden"
          borderRadius="inherit"
        >
          <Image
            src={mediaItem.url}
            alt="Post image"
            sx={mediaElementStyle}
            _hover={hoverStyle}
          />
        </AspectRatio>
      );
    } else if (mediaItem.type === "video") {
      return (
        <AspectRatio
          ratio={16 / 9}
          w="100%"
          h="100%"
          overflow="hidden"
          borderRadius="inherit"
        >
          <Box
            as="video"
            src={mediaItem.url}
            controls
            sx={mediaElementStyle}
            _hover={hoverStyle}
          />
        </AspectRatio>
      );
    }
    return <Text>Unsupported media type</Text>;
  }
);

interface PostCardProps {
  post: ForumPost;
  currentDoctorId?: string;
  onNavigateToDetail?: (postId: string, hash?: string) => void;
  isDetailPage?: boolean;
  onRequestScrollToComments?: () => void;
  commentInputRef?: React.RefObject<HTMLInputElement>;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentDoctorId,
  onNavigateToDetail,
  isDetailPage,
  onRequestScrollToComments,
  commentInputRef,
}) => {
  const navigate = useNavigate();
  const [commentText, setCommentText] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalMediaItems, setModalMediaItems] = useState<TMediaItem[]>([]);
  const [currentModalImageIndex, setCurrentModalImageIndex] = useState(0);

  const [optimisticIsLiked, setOptimisticIsLiked] = useState(false);
  useEffect(() => {
    if (currentDoctorId && post.upvotedBy?.includes(currentDoctorId)) {
      setOptimisticIsLiked(true);
    } else {
      setOptimisticIsLiked(false);
    }
  }, [post.upvotedBy, currentDoctorId]);

  const timeAgo = post.createdAt
    ? formatDistanceToNow(parseISO(post.createdAt), { addSuffix: true })
    : "just now";
  const authorName = post.author?.name || "Anonymous User";

  const specialty = post.author?.specialty?.[0];
  const firstClinic = post.author?.clinics?.[0];
  let clinicLocationString = "";
  if (firstClinic) {
    const city = firstClinic.city;
    const state = firstClinic.state;
    if (city && state) clinicLocationString = `${city}, ${state}`;
    else if (city) clinicLocationString = city;
    else if (state) clinicLocationString = state;
  }
  let finalAuthorLocation = "";
  if (specialty && clinicLocationString)
    finalAuthorLocation = `${specialty} in ${clinicLocationString}`;
  else if (specialty) finalAuthorLocation = specialty;
  else if (clinicLocationString) finalAuthorLocation = clinicLocationString;
  else finalAuthorLocation = "";
  const authorLocation = finalAuthorLocation;

  const isAuthorVerified = post.author?.isVerified || false;
  const displayTitle = post.title;
  const displayContent = post.content;
  const formattedViewsCount = formatCount(post.viewCount);

  const { data: postData } = useGetForumPostById(post._id);
  const { data: commentsData } = useGetCommentsForPost(post._id);

  const commentsToDisplay: CommentWithFullAuthor[] = commentsData || [];

  const repliesCount =
    postData?.repliesCount !== undefined
      ? postData.repliesCount
      : commentsToDisplay.length;
  const isAuthoredByCurrentUser =
    !!currentDoctorId && post.author?._id === currentDoctorId;
  const hasLikedByCurrentUser = optimisticIsLiked;
  const hasCommentedByCurrentUser =
    !!currentDoctorId &&
    (post.replies?.some((reply) => reply.author?._id === currentDoctorId) ||
      false);

  const toast = useToast();

  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Bind to THIS post
  const { mutate: createReply, isPending: isPosting } = useCreateReply(
    post._id
  );

  // (Optional) keep a local replies counter so UI feels instant
  const [localRepliesCount, setLocalRepliesCount] = useState<number>(
    post.repliesCount ?? 0
  );
  useEffect(() => {
    setLocalRepliesCount(post.repliesCount ?? 0);
  }, [post.repliesCount]);

  const handleMainContentClick = () => {
    if (onNavigateToDetail) {
      onNavigateToDetail(post._id);
    } else if (!isDetailPage && post?.author?._id && post?._id) {
      navigate(`/meet/${post.author._id}/posts/${post._id}`);
    }
  };

  const handleLikeClick = () => {
    setOptimisticIsLiked(!optimisticIsLiked);
  };

  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCommentText(event.target.value);
    },
    []
  );

  const handleEmojiSelect = useCallback((emoji: string) => {
    setCommentText((prev) => prev + emoji);
  }, []);

  const handleSendComment = useCallback(() => {
    const trimmed = commentText.trim();
    if (!trimmed || isPosting) return;

    createReply(
      {
        content: trimmed,
        // parentReply: "someReplyIdIfThreaded", // omit if top-level
        mediaFiles: attachments.length ? attachments : undefined,
      },
      {
        onSuccess: () => {
          setCommentText("");
          setAttachments([]);
          setLocalRepliesCount((c) => c + 1); // optimistic bump
          toast({
            status: "success",
            title: "Reply posted",
            duration: 2000,
            isClosable: true,
          });
        },
        onError: (err: any) => {
          toast({
            status: "error",
            title: "Failed to post reply",
            description: err?.message ?? "Please try again",
            duration: 3000,
            isClosable: true,
          });
        },
      }
    );
  }, [commentText, attachments, isPosting, createReply, toast]);

  const handleMediaClick = useCallback(
    (clickedIndex: number) => {
      if (post.media) {
        setModalMediaItems(post.media);
        setCurrentModalImageIndex(clickedIndex);
        onOpen();
      }
    },
    [post.media, onOpen]
  );

  const handleModalNext = useCallback(() => {
    setCurrentModalImageIndex(
      (prevIndex) => (prevIndex + 1) % modalMediaItems.length
    );
  }, [modalMediaItems]);

  const handleModalPrev = useCallback(() => {
    setCurrentModalImageIndex(
      (prevIndex) =>
        (prevIndex - 1 + modalMediaItems.length) % modalMediaItems.length
    );
  }, [modalMediaItems]);

  const handleRepliesButtonClick = () => {
    if (isDetailPage && onRequestScrollToComments) {
      onRequestScrollToComments();
    } else {
      const targetPostId = post._id;
      const commentsHash = "#comments-section";
      if (onNavigateToDetail) {
        onNavigateToDetail(targetPostId, commentsHash);
      } else if (post?.author?._id && targetPostId) {
        navigate(
          `/meet/${post.author._id}/posts/${targetPostId}${commentsHash}`
        );
      }
    }
  };

  const renderMediaLayout = () => {
    if (!post.media || post.media.length === 0) {
      return null;
    }
    const mediaItems = post.media;
    const numMedia = mediaItems.length;

    if (numMedia === 1) {
      return (
        <Box
          w="100%"
          h="auto"
          maxH={{ base: "300px", md: "400px" }}
          onClick={() => handleMediaClick(0)}
          cursor="pointer"
          borderRadius="xl"
          overflow="hidden"
        >
          <MediaDisplay mediaItem={mediaItems[0]} />
        </Box>
      );
    } else if (numMedia === 2) {
      return (
        <HStack
          spacing={2}
          w="100%"
          maxH={{ base: "200px", md: "300px" }}
          borderRadius="xl"
          overflow="hidden"
        >
          {mediaItems.map((item, index) => (
            <Box
              key={item._id}
              flex="1"
              h="100%"
              onClick={() => handleMediaClick(index)}
              cursor="pointer"
            >
              <MediaDisplay mediaItem={item} />
            </Box>
          ))}
        </HStack>
      );
    } else if (numMedia === 3) {
      return (
        <VStack
          spacing={2}
          w="100%"
          maxH={{ base: "450px", md: "600px" }}
          borderRadius="xl"
          overflow="hidden"
        >
          <Box
            w="100%"
            h={{ base: "200px", md: "300px" }}
            onClick={() => handleMediaClick(0)}
            cursor="pointer"
          >
            <MediaDisplay mediaItem={mediaItems[0]} />
          </Box>
          <HStack
            spacing={2}
            w="100%"
            flex="1"
            h={{ base: "120px", md: "200px" }}
          >
            {mediaItems.slice(1, 3).map((item, index) => (
              <Box
                key={item._id}
                flex="1"
                h="100%"
                onClick={() => handleMediaClick(index + 1)}
                cursor="pointer"
              >
                <MediaDisplay mediaItem={item} />
              </Box>
            ))}
          </HStack>
        </VStack>
      );
    } else if (numMedia >= 4) {
      const visibleMedia = mediaItems.slice(0, 4);
      const remainingCount = numMedia - 4;
      return (
        <SimpleGrid
          columns={2}
          spacing={2}
          w="100%"
          maxH={{ base: "300px", md: "400px" }}
          borderRadius="xl"
          overflow="hidden"
        >
          {visibleMedia.map((item, index) => (
            <Box
              key={item._id}
              h={{ base: "150px", md: "200px" }}
              position="relative"
              onClick={() => handleMediaClick(index)}
              cursor="pointer"
            >
              <MediaDisplay mediaItem={item} />
              {index === 3 && remainingCount > 0 && (
                <Flex
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="blackAlpha.600"
                  color="white"
                  fontSize="2xl"
                  fontWeight="bold"
                  justifyContent="center"
                  alignItems="center"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMediaClick(index);
                  }}
                >
                  +{remainingCount}
                </Flex>
              )}
            </Box>
          ))}
        </SimpleGrid>
      );
    }
    return null;
  };

  const getBackgroundStyle = () => {
    const tints: string[] = [];
    const authoredColor = "rgba(144, 238, 144, 0.15)";
    const likedColor = "rgba(255, 192, 203, 0.15)";
    const commentedColor = "rgba(173, 216, 230, 0.15)";

    if (isAuthoredByCurrentUser) tints.push(authoredColor);
    if (hasLikedByCurrentUser) tints.push(likedColor);
    if (hasCommentedByCurrentUser) tints.push(commentedColor);

    if (tints.length === 0) return "white";
    if (tints.length === 1) return tints[0];
    return `linear-gradient(to right, ${tints.join(", ")})`;
  };

  return (
    <Box
      p={{ base: 3, md: 5 }}
      width="100%"
      maxWidth={{ base: "100%", md: "900px" }}
      mx="auto"
      bg={getBackgroundStyle()}
      borderRadius="xl"
      boxShadow="sm"
      mb={4}
      transition="background 0.3s ease"
    >
      <Flex justify="space-between" align="center" mb={3}>
        <Flex align="center">
          <Avatar
            name={authorName}
            src={post.author?.avatarUrl || undefined}
            size="xs"
            mr={2}
          />
          <Flex align="center" gap={1} wrap="wrap">
            <Text fontWeight="bold" fontSize="sm" color="black" noOfLines={1}>
              {authorName}
            </Text>
            {isAuthorVerified && (
              <Icon as={VerifiedIcon} color="blue.500" boxSize={3} />
            )}
            {authorLocation && (
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {authorLocation}
              </Text>
            )}
            <Text fontSize="xs" color="gray.500">
              • {timeAgo}
            </Text>
          </Flex>
        </Flex>
        <HStack spacing={0.5} align="center">
          <Text fontSize="xs" color="gray.600" fontWeight="semibold">
            {formattedViewsCount}
          </Text>
          <Icon as={VisibilityIcon} color="gray.600" boxSize={3} />
        </HStack>
      </Flex>

      <Box
        onClick={handleMainContentClick}
        cursor={
          onNavigateToDetail ||
          (!isDetailPage && post?.author?._id && post?._id)
            ? "pointer"
            : "default"
        }
      >
        {displayTitle && (
          <Heading
            as="h3"
            size="md"
            mb={2}
            color="gray.800"
            _hover={{ color: "blue.600" }}
            noOfLines={2}
          >
            {displayTitle}
          </Heading>
        )}
        <Text
          color="gray.700"
          fontSize="md"
          lineHeight="tall"
          mb={4}
          _hover={{ color: "blue.700" }}
          noOfLines={4}
        >
          {displayContent}
        </Text>
      </Box>

      {renderMediaLayout()}

      {post.tags &&
        post.tags.filter((tag) => tag && tag.trim() !== "").length > 0 && (
          <Flex
            wrap="wrap"
            gap={2}
            mt={post.media && post.media.length > 0 ? 4 : 2}
            mb={4}
          >
            {post.tags
              .filter((tag) => tag && tag.trim() !== "")
              .map((tag) => (
                <Tag
                  size="md"
                  key={tag}
                  variant="solid"
                  backgroundColor="gray.100"
                  color="gray.600"
                  fontWeight="medium"
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {tag}
                </Tag>
              ))}
          </Flex>
        )}

      <Divider my={4} borderColor="gray.200" />

      <Flex align="center" gap={3}>
        <Button
          variant="solid"
          backgroundColor="gray.800"
          color="white"
          fontWeight="medium"
          size="md"
          borderRadius="full"
          px={4}
          py={2}
          _hover={{ bg: "gray.900", cursor: "pointer" }}
          onClick={handleRepliesButtonClick}
        >
          <>
            <Text as="span" fontWeight="bold">
              {repliesCount}
            </Text>{" "}
            <Text as="span" ml={1}>
              replies
            </Text>
          </>
        </Button>

        <InputGroup size="md" flex={1}>
          <Input
            ref={commentInputRef}
            placeholder="What do you think?"
            borderRadius="full"
            bg="gray.50"
            borderColor="gray.200"
            _focus={{ borderColor: "blue.300", boxShadow: "0 0 0 1px #90CDF4" }}
            value={commentText}
            onChange={handleCommentChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendComment();
              }
            }}
            pr={commentText.trim().length > 0 ? "calc(3rem + 28px)" : "3rem"}
          />
          <InputRightElement
            width="auto"
            right={commentText.trim().length > 0 ? "4px" : "4px"}
            pointerEvents="auto"
          >
            <HStack spacing={1} alignItems="center" height="100%">
              <Popover placement="top-end">
                <PopoverTrigger>
                  <IconButton
                    aria-label="Add emoji"
                    icon={
                      <Icon
                        as={SentimentSatisfiedIcon}
                        boxSize={5}
                        color="gray.500"
                      />
                    }
                    size="sm"
                    variant="ghost"
                    isRound
                  />
                </PopoverTrigger>
                <PopoverContent
                  width="auto"
                  minW="200px"
                  _focus={{ boxShadow: "none" }}
                >
                  <PopoverArrow />
                  <PopoverBody>
                    <SimpleGrid columns={5} spacing={2}>
                      {commonEmojis.map((emoji) => (
                        <Button
                          key={emoji}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEmojiSelect(emoji)}
                          fontSize="xl"
                          p={0}
                          w="30px"
                          h="30px"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          {emoji}
                        </Button>
                      ))}
                    </SimpleGrid>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
              {commentText.trim().length > 0 && (
                <IconButton
                  aria-label="Send comment"
                  icon={<ArrowUpIcon />}
                  size="sm"
                  isRound
                  bg="gray.900"
                  color="white"
                  _hover={{ bg: "black" }}
                  onClick={handleSendComment}
                />
              )}
            </HStack>
          </InputRightElement>
        </InputGroup>

        <IconButton
          aria-label="Like post"
          icon={
            hasLikedByCurrentUser ? (
              <FavoriteIconReact />
            ) : (
              <FavoriteBorderIconReact />
            )
          }
          variant="ghost"
          size="md"
          isRound
          onClick={handleLikeClick}
          color={hasLikedByCurrentUser ? "red.500" : "gray.600"}
        />
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent
          bg="transparent"
          boxShadow="none"
          maxW="unset"
          maxH="unset"
        >
          <Flex
            position="absolute"
            top={{ base: "10px", md: "20px" }}
            left={{ base: "10px", md: "20px" }}
            right={{ base: "10px", md: "20px" }}
            zIndex="overlay"
            alignItems="center"
            justifyContent="space-between"
          >
            <HStack spacing={3} alignItems="center">
              <Avatar
                name={authorName}
                src={post.author?.avatarUrl || undefined}
                size="sm"
              />
              <VStack align="flex-start" spacing={0}>
                <Text
                  fontWeight="bold"
                  fontSize={{ base: "sm", md: "md" }}
                  color="white"
                >
                  {modalMediaItems[currentModalImageIndex]?.type === "video"
                    ? "Video"
                    : "Image"}{" "}
                  from {authorName}
                </Text>
                {authorLocation && (
                  <HStack spacing={1}>
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                      {authorLocation}
                    </Text>
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                      • {timeAgo}
                    </Text>
                  </HStack>
                )}
                {!authorLocation && (
                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    {timeAgo}
                  </Text>
                )}
              </VStack>
            </HStack>
            <ModalCloseButton
              color="white"
              size="lg"
              _hover={{ bg: "whiteAlpha.200" }}
              position="static"
            />
          </Flex>

          <ModalBody
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={0}
            position="relative"
            h="100vh"
            w="100vw"
          >
            {modalMediaItems.length > 0 &&
              modalMediaItems[currentModalImageIndex] &&
              (modalMediaItems[currentModalImageIndex]?.type === "image" ? (
                <Image
                  src={modalMediaItems[currentModalImageIndex].url}
                  alt="Enlarged preview"
                  maxH="90vh"
                  maxW="90vw"
                  objectFit="contain"
                  onClick={onClose}
                  cursor="pointer"
                />
              ) : modalMediaItems[currentModalImageIndex]?.type === "video" ? (
                <video
                  src={modalMediaItems[currentModalImageIndex].url}
                  controls
                  autoPlay
                  style={{
                    maxHeight: "90vh",
                    maxWidth: "90vw",
                    objectFit: "contain",
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <Text color="white">Unsupported media type</Text>
              ))}

            {modalMediaItems.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous image"
                  icon={
                    <Icon
                      as={ChevronLeftIconReact}
                      boxSize={{ base: 6, md: 8 }}
                    />
                  }
                  position="absolute"
                  left={{ base: "5px", md: "20px" }}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex="overlay"
                  bg="whiteAlpha.300"
                  color="white"
                  borderRadius="full"
                  size={{ base: "md", md: "lg" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalPrev();
                  }}
                  _hover={{ bg: "whiteAlpha.500" }}
                />
                <IconButton
                  aria-label="Next image"
                  icon={
                    <Icon
                      as={ChevronRightIconReact}
                      boxSize={{ base: 6, md: 8 }}
                    />
                  }
                  position="absolute"
                  right={{ base: "5px", md: "20px" }}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex="overlay"
                  bg="whiteAlpha.300"
                  color="white"
                  borderRadius="full"
                  size={{ base: "md", md: "lg" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalNext();
                  }}
                  _hover={{ bg: "whiteAlpha.500" }}
                />
              </>
            )}

            {modalMediaItems.length > 1 && (
              <HStack
                mt={4}
                spacing={2}
                position="absolute"
                bottom={{ base: "10px", md: "20px" }}
              >
                {modalMediaItems.map((_, index) => (
                  <Box
                    key={index}
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={index === currentModalImageIndex ? "white" : "gray.500"}
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentModalImageIndex(index);
                    }}
                  />
                ))}
              </HStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default React.memo(PostCard);
